import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaTimes, FaVideo, FaComment, FaPhone } from "react-icons/fa";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { useDispatch, useSelector } from "react-redux";
import {
  createBooking,
  getAvailableTimeSlots,
} from "../../../redux/actions/mindfulness";
import { handleSuccessMessage } from "../../../redux/actions/success";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  width: 100%;
  /* max-width: 600px; */
  max-height: 80vh;
  position: relative;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const CloseButton = styled.p`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background: #f8fafc;
    color: #4a5568;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #edf2f7;
  font-family: Montserrat;
`;

const HeaderTitle = styled.p`
  font-size: 1.5rem;
  font-weight: 400;
  color: #2d3748;
  margin: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #edf2f7;
  flex-shrink: 0;
`;

const CreatorProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProfileImage = styled.img`
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
  flex-shrink: 0;
`;

const CreatorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CreatorName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  text-transform: capitalize;
`;

const CreatorTitle = styled.p`
  font-size: 0.95rem;
  color: #718096;
  margin: 0;
`;

const SessionTypeBadge = styled.span`
  background: #e6f7ff;
  color: #1890ff;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  align-self: flex-start;
  margin-top: 4px;
  border: 1px solid #bae6fd;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  font-family: Montserrat;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 8px;
`;

const SessionTypeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const SessionTypeOption = styled.button`
  background: ${(props) => (props.isSelected ? "#e6f7ff" : "#f8fafc")};
  border: ${(props) =>
    props.isSelected ? "1px solid #1890ff" : "1px solid #cbd5e1"};
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;

  &:hover {
    background: #edf2f7;
    border-color: #94a3b8;
  }

  svg {
    font-size: 1.2rem;
    color: ${(props) => (props.isSelected ? "#1890ff" : "#4a5568")};
  }

  .price {
    font-weight: 600;
    font-size: 1.1rem;
    color: ${(props) => (props.isSelected ? "#e53e3e" : "#4a5568")};
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #1890ff;
  }
`;

const DateSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  cursor: pointer;
  color: #2d3748;

  &:focus {
    border-color: #1890ff;
  }

  option {
    color: #2d3748;
    background-color: #f8fafc;
    font-size: 0.95rem;
    font-weight: 400;
    padding: 0.5rem;
  }
`;

const DropdownOption = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  color: #2d3748;
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;

  &:hover {
    background: #e2e8f0;
  }
`;

const NoSlotsMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: #991b1b;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const TimeSlots = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`;

const TimeSlot = styled.button`
  background: ${(props) => (props.isSelected ? "#1890ff" : "#f8fafc")};
  border: ${(props) =>
    props.isSelected ? "2px solid #1890ff" : "1px solid #cbd5e1"};
  border-radius: 8px;
  padding: 10px;
  font-size: 0.95rem;
  color: ${(props) => (props.isSelected ? "white" : "#4a5568")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.isSelected ? "#1890ff" : "#edf2f7")};
    border-color: ${(props) => (props.isSelected ? "#1890ff" : "#94a3b8")};
  }
`;

const SessionDetails = styled.div`
  background: #fff6f6;
  border: 1px solid #fed8d8;
  border-radius: 10px;
  padding: 16px;
`;

const DetailsGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 0;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.dt`
  font-size: 0.85rem;
  color: #718096;
  margin-bottom: 4px;
  font-weight: 500;
`;

const DetailValue = styled.dd`
  font-size: 1rem;
  color: #2d3748;
  margin: 0;
`;

const TotalAmount = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  color: #e53e3e;
  text-align: right;
  margin-top: 8px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  display: flex;
  gap: 12px;
  border-top: 1px solid #edf2f7;
  justify-content: flex-end;
  flex-shrink: 0;
`;
const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.cancel {
    background: #f8fafc;
    color: #4a5568;
    border: 1px solid #cbd5e1;

    &:hover:not(:disabled) {
      background: #edf2f7;
      border-color: #94a3b8;
    }

    &:disabled {
      background: #f8fafc;
      color: #a0aec0;
      border-color: #e2e8f0;
    }
  }

  &.proceed {
    background: #e53e3e;
    color: white;

    &:hover:not(:disabled) {
      background: #c53030;
    }

    &:disabled {
      background: #f7fafc;
      border: "1px solid black";
      color: #a0aec0;
    }
  }
`;

// Main Component
const BookingModal = ({ session, onClose }) => {
  // State management
  const [selectedSessionType, setSelectedSessionType] = useState("chat");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const dispatch = useDispatch();
  const availableTimeSlots = useSelector(
    (state) => state.mindfulness.availableTimeSlots,
  );

  const history = useHistory();

  console.log("redux timeslot", availableTimeSlots);

  // Session type labels
  const sessionTypeLabels = {
    chat: "Chat Session",
    call: "Voice Call",
    video_call: "Video Session",
  };

  // Format time from ISO string
  // const formatTime = (isoString) => {
  //   const date = new Date(isoString);
  //   const hours = date.getHours();
  //   const minutes = date.getMinutes();
  //   const period = hours >= 12 ? "PM" : "AM";
  //   const displayHour = hours % 12 || 12;
  //   const displayMinutes = minutes.toString().padStart(2, "0");
  //   return `${displayHour}:${displayMinutes} ${period}`;
  // };

  const formatTime = (dateStr) => {
    // const dateStr = '2026-02-02T09:00:00+00:00';
    const timeOnly = moment(dateStr).format("h:mm A");
    console.log("returning Time", { dateStr, timeOnly });
    return timeOnly;
  };

  // Format date for display
  // const formatDateDisplay = (dateString) => {
  //   const date = new Date(dateString);
  //   const options = { month: "short", day: "numeric", year: "numeric" };
  //   return date.toLocaleDateString("en-US", options);
  // };

  const formatDateDisplay = (dateStr) => {
    return moment(dateStr).format("DD-MM-YYYY");
  };

  // Get available dates from weekly_slots
  const getAvailableDates = () => {
    return availableTimeSlots?.weekly_slots || [];
  };

  // Get selected date data
  const getSelectedDateData = () => {
    if (!selectedDate) return null;
    return availableTimeSlots?.weekly_slots?.find(
      (slot) => slot.date === selectedDate,
    );
  };

  // Get slots for selected date
  const getAvailableSlots = () => {
    const dateData = getSelectedDateData();
    return dateData?.available_slots || [];
  };

  // Get price for selected session type
  const getPrice = () => {
    const prices = session.conversation_type_amount || {};
    return prices[selectedSessionType] || 0;
  };

  // Get session type label
  const getSessionTypeLabel = () => {
    return sessionTypeLabels[selectedSessionType] || "Session";
  };

  useEffect(async () => {
    if (session.id) {
      let res = await dispatch(
        getAvailableTimeSlots({ creator_session: session.id }),
      );
      console.log("timeSlots", res.data);
    }
  }, [session]);

  const handleProceedToPayment = async () => {
    let payload = {
      creator_session: session.id,
      start_time: selectedSlot,
      duration: session.duration,
      type: selectedSessionType,
    };
    console.log("paymentPayload", payload);
    let res = await dispatch(createBooking(payload));
    let isPaymentSuccessful = true;
    onClose(isPaymentSuccessful);
    history.push("/user_bookings")
  };

  console.log("bookingModalData", session);
  console.log("booking data", {
    selectedDate,
    selectedSessionType,
    selectedSlot,
    formattedSlot: selectedSlot ? formatTime(selectedSlot) : null,
  });
  return (
    <Dialog
      open={true}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <HeaderContainer>
          <HeaderTitle>Book Your Session</HeaderTitle>
          <CloseButton onClick={() => onClose(false)}>
            <FaTimes />
          </CloseButton>
        </HeaderContainer>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <ModalBody>
          <ModalHeader>
            <CreatorProfile>
              <ProfileImage
                src={
                  session.creator?.profile_pic?.trim() ||
                  "https://via.placeholder.com/60"
                }
                alt={session.creator_name}
              />
              <CreatorInfo>
                <CreatorName>{session.creator_name}</CreatorName>
                <CreatorTitle>
                  {session.creator?.designation || "Creator"}
                </CreatorTitle>
              </CreatorInfo>
            </CreatorProfile>

            <SessionTypeBadge>
              {getSessionTypeLabel()} {session.duration} mins
            </SessionTypeBadge>
          </ModalHeader>

          <Section>
            <SectionTitle>Select Session type</SectionTitle>
            <SessionTypeOptions>
              {/* {["chat", "call", "video_call"].map((type) => ( */}
              {[...Object.keys(session.conversation_type_amount || {})].map((type) => (
                <SessionTypeOption
                  key={type}
                  isSelected={selectedSessionType === type}
                  onClick={() => setSelectedSessionType(type)}
                >
                  {type === "chat" && <FaComment />}
                  {type === "call" && <FaPhone />}
                  {type === "video_call" && <FaVideo />}
                  <span>{sessionTypeLabels[type]}</span>
                  <span className="price">
                    ₹{session.conversation_type_amount[type] || 0}
                  </span>
                </SessionTypeOption>
              ))}
            </SessionTypeOptions>
          </Section>

          <Section>
            <SectionTitle>Select Date</SectionTitle>
            <DateSelect
              value={selectedDate || ""}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setSelectedSlot(null); // Reset selected slot
              }}
            >
              <option value="" disabled>
                Choose a date
              </option>
              {getAvailableDates().map((dateSlot) => (
                <option key={dateSlot.date} value={dateSlot.date}>
                  {formatDateDisplay(dateSlot.date)} - {dateSlot.day_name} (
                  {dateSlot.available_count} slots available)
                </option>
              ))}
            </DateSelect>
          </Section>

          {selectedDate && getAvailableSlots().length > 0 && (
            <Section>
              <SectionTitle>Select Time Slot</SectionTitle>
              <TimeSlots>
                {getAvailableSlots().map((slot, index) => (
                  <TimeSlot
                    key={index}
                    isSelected={selectedSlot === slot.start_time}
                    onClick={() => setSelectedSlot(slot.start_time)}
                  >
                    {formatTime(slot.start_time)}
                  </TimeSlot>
                ))}
              </TimeSlots>
            </Section>
          )}

          {selectedDate && getAvailableSlots().length === 0 && (
            <Section>
              <NoSlotsMessage>
                {getSelectedDateData()?.message ||
                  "No slots available for this date"}
              </NoSlotsMessage>
            </Section>
          )}

          <Section>
            <SectionTitle>Session Details</SectionTitle>
            <SessionDetails>
              <DetailsGrid>
                <DetailItem>
                  <DetailLabel>Session Type:</DetailLabel>
                  <DetailValue>{getSessionTypeLabel()}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Duration:</DetailLabel>
                  <DetailValue>{session.duration} minutes</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Category:</DetailLabel>
                  <DetailValue>{session.category}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Total Amount:</DetailLabel>
                  <TotalAmount>₹{getPrice()}</TotalAmount>
                </DetailItem>
              </DetailsGrid>
            </SessionDetails>
          </Section>
        </ModalBody>
      </DialogContent>

      <DialogActions sx={{ p: 0 }}>
        <ModalFooter>
          <Button className="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="proceed"
            onClick={() => handleProceedToPayment()}
            disabled={!selectedSlot}
          >
            Proceed to Payment
          </Button>
        </ModalFooter>
      </DialogActions>
    </Dialog>
  );
};

export default BookingModal;
